import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";
import { uploadFileToStorage } from "@/lib/storageUrl";
import { planoIncluiLimpeza } from "@/lib/planoLimpeza";

export interface Orcamento {
  id: number;
  cliente_id: number;
  projeto_id?: number;
  vendedor_id?: string;
  valor_total: number;
  status: string;
  observacoes?: string;
  plano_id?: string;
  quantidade_placas?: number;
  valor_seguro?: number;
  vigencia_anos?: number;
  forma_pagamento?: string;
  parcelas_cartao?: number;
  numero_parcelas?: number;
  prazo_aprovacao?: string;
  data_aprovacao?: string;
  aprovado_por?: string;
  ativo: boolean;
  motivo_inativacao?: string;
  data_inativacao?: string;
  vistoria_urls?: Array<{ url: string; nome: string }>;
  motivo_rejeicao_vistoria?: string | null;
  pedido_origem_id?: number | null;
  numero_ciclo?: number | null;
  valor_tabela?: number | null;
  desconto_valor?: number | null;
  desconto_concedido_por?: string | null;
  desconto_motivo?: string | null;
  desconto_aplicado_em?: string | null;
  plano?: {
    id: string;
    nome: string;
    descricao?: string;
    valor_por_painel: number;
    vigencia_meses: number;
    ativo: boolean;
  };
  created_at: string;
  updated_at: string;
  cliente?: {
    id: number;
    nome: string;
    cpf_cnpj: string;
    email?: string;
    telefone?: string;
    endereco?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
  };
  equipamentos?: Array<{
    id: number;
    equipamento_id: number;
    quantidade: number;
    preco_unitario?: number;
    observacoes?: string;
    equipamento?: {
      id: number;
      tipo: string;
      marca: string;
      modelo: string;
      potencia?: string;
      preco_unitario?: number;
    };
  }>;
  projeto?: {
    id: number;
    equipamentos?: Array<{
      id: number;
      equipamento_id: number;
      quantidade: number;
      preco_unitario?: number;
      equipamento?: {
        id: number;
        tipo: string;
        marca: string;
        modelo: string;
        potencia?: string;
        preco_unitario?: number;
      };
    }>;
  } | null;
}

export type OrcamentosFiltroParams = {
  periodo?: { dataInicio: string; dataFim: string } | null;
  planoId?: string | null;
  status?: string | null;
  estado?: string | null; // cliente.estado (UF sigla)
  cidade?: string | null; // cliente.cidade
  valorProjeto?: { min?: number; max?: number } | null; // orcamentos.valor_total
  valorSeguro?: { min?: number; max?: number } | null; // orcamentos.valor_seguro
  paineis?: { min?: number; max?: number } | null; // orcamentos.quantidade_placas
};

export type OrcamentosSort = {
  field:
    | "id"
    | "created_at"
    | "status"
    | "valor_total"
    | "valor_seguro"
    | "quantidade_placas"
    | "plano_id"
    | "cliente_nome";
  direction: "asc" | "desc";
};

// Hook para contar total de orçamentos ativos
export function useOrcamentosCount() {
  const { user, profile, loading } = useAuth();
  const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['orcamentos', 'count', isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase
        .from('orcamentos')
        .select('*', { count: 'exact', head: true })
        .eq('ativo', true);
      if (isVendedor) query = query.eq('vendedor_id', user!.id);
      const { count, error } = await query;

      if (error) {
        console.error('❌ Erro ao contar orçamentos:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !loading,
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar orçamentos paginados
export function useOrcamentosPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  filtros?: OrcamentosFiltroParams | null,
  sort?: OrcamentosSort | null
) {
  const { user, profile, loading } = useAuth();
  const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['orcamentos', 'paginated', page, limit, searchTerm, filtros ?? null, sort ?? null, isVendedor ? user?.id : null],
    queryFn: async () => {
      console.log(`🔍 Buscando orçamentos - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        // Detectar inner join no cliente quando há filtro por estado/cidade
        const clienteFilterPresent = !!(filtros?.estado || filtros?.cidade);
        const clienteEmbed = `cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cpf_cnpj, endereco, numero, bairro, cidade, estado, cep)`;

        // Construir query base
        let query = supabase
          .from('orcamentos')
          .select(`
            *,
            ${clienteEmbed},
            plano:planos_seguro(*),
            equipamentos:orcamento_equipamentos(
              *,
              equipamento:equipamentos(*)
            )
          `, { count: 'exact' })
          .eq('ativo', true); // Filtrar apenas orçamentos ativos

        if (isVendedor) query = query.eq('vendedor_id', user!.id);

        // Aplicar filtros (relatórios)
        const periodo = filtros?.periodo ?? null;
        if (periodo?.dataInicio) {
          query = query.gte('created_at', `${periodo.dataInicio}T00:00:00`);
        }
        if (periodo?.dataFim) {
          query = query.lte('created_at', `${periodo.dataFim}T23:59:59`);
        }

        const planoId = (filtros?.planoId ?? "").trim();
        if (planoId) {
          query = query.eq('plano_id', planoId);
        }

        const status = (filtros?.status ?? "").trim();
        if (status) {
          query = query.eq('status', status);
        }

        // Estado / Cidade (cliente.estado / cliente.cidade) — exige inner no cliente
        if (filtros?.estado) {
          query = query.eq('cliente.estado', filtros.estado.toUpperCase());
        }
        if (filtros?.cidade) {
          query = query.eq('cliente.cidade', filtros.cidade);
        }

        const valorProjeto = filtros?.valorProjeto ?? null;
        if (typeof valorProjeto?.min === 'number' && !Number.isNaN(valorProjeto.min)) {
          query = query.gte('valor_total', valorProjeto.min);
        }
        if (typeof valorProjeto?.max === 'number' && !Number.isNaN(valorProjeto.max)) {
          query = query.lte('valor_total', valorProjeto.max);
        }

        const valorSeguro = filtros?.valorSeguro ?? null;
        if (typeof valorSeguro?.min === 'number' && !Number.isNaN(valorSeguro.min)) {
          query = query.gte('valor_seguro', valorSeguro.min);
        }
        if (typeof valorSeguro?.max === 'number' && !Number.isNaN(valorSeguro.max)) {
          query = query.lte('valor_seguro', valorSeguro.max);
        }

        const paineis = filtros?.paineis ?? null;
        if (typeof paineis?.min === 'number' && !Number.isNaN(paineis.min)) {
          query = query.gte('quantidade_placas', paineis.min);
        }
        if (typeof paineis?.max === 'number' && !Number.isNaN(paineis.max)) {
          query = query.lte('quantidade_placas', paineis.max);
        }

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim();
          
          // Se for número, buscar por ID do orçamento
          if (!isNaN(Number(searchLower))) {
            query = query.eq('id', parseInt(searchLower));
          } else {
            // Se for texto, buscar clientes que correspondem e filtrar orçamentos por cliente_id
            let clientesQuery = supabase.from('clientes').select('id').ilike('nome', `%${searchLower}%`);
            if (isVendedor) clientesQuery = clientesQuery.eq('vendedor_id', user!.id);
            const { data: clientesEncontrados } = await clientesQuery;
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.in('cliente_id', clienteIds);
            } else {
              query = query.eq('id', -1); // ID que não existe
            }
          }
        }

        // Ordenar e paginar
        const sortField = sort?.field || "created_at";
        const sortAsc = (sort?.direction || "desc") === "asc";
        if (sortField === "cliente_nome") {
          query = query.order("nome", { ascending: sortAsc, foreignTable: "cliente" }).range(from, to);
        } else {
          query = query.order(sortField, { ascending: sortAsc }).range(from, to);
        }

        const { data: orcamentosData, error: orcamentosError, count } = await query;

        if (orcamentosError) {
          console.error('❌ Erro ao buscar orçamentos:', orcamentosError);
          throw orcamentosError;
        }

        console.log(`✅ Orçamentos encontrados na página ${page}:`, orcamentosData?.length || 0, `Total: ${count || 0}`);

        // Para orçamentos sem equipamentos diretos, buscar por projeto se existir
        const orcamentosRaw = ((orcamentosData || []) as unknown) as any[];
        const orcamentosCompletos = await Promise.all(
          orcamentosRaw.map(async (orcamento: any) => {
            // Se já tem equipamentos do orçamento, retornar direto
            if (orcamento.equipamentos && orcamento.equipamentos.length > 0) {
              return {
                ...orcamento,
                projeto: orcamento.projeto_id ? { id: orcamento.projeto_id } : null,
              } as Orcamento;
            }

            // Se não tem equipamentos no orçamento mas tem projeto_id, buscar equipamentos do projeto
            if (orcamento.projeto_id) {
              const { data: eqProjeto } = await supabase
                .from('projeto_equipamentos')
                .select(`
                  *,
                  equipamento:equipamentos(*)
                `)
                .eq('projeto_id', orcamento.projeto_id)
                .limit(50); // Limitar equipamentos por projeto

              return {
                ...orcamento,
                equipamentos: eqProjeto || [],
                projeto: {
                  id: orcamento.projeto_id,
                  equipamentos: eqProjeto || [],
                },
              } as Orcamento;
            }

            // Sem equipamentos e sem projeto
            return {
              ...orcamento,
              equipamentos: [],
              projeto: null,
            } as Orcamento;
          })
        );

        console.log('✅ Orçamentos completos processados:', orcamentosCompletos.length);
        return { data: orcamentosCompletos as Orcamento[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de orçamentos:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // 30 segundos
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
    enabled: !loading,
  });
}

// Hook legado - mantido para compatibilidade, mas agora usa paginação
export function useOrcamentos() {
  return useOrcamentosPaginated(1, 15);
}

export function useCriarOrcamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dados: {
      cliente_id: number;
      projeto_id?: number;
      valor_total: number;
      observacoes?: string;
      plano_id?: string;
      quantidade_placas?: number;
      valor_seguro?: number;
      vigencia_anos?: number;
      forma_pagamento?: string | null;
      parcelas_cartao?: number | null;
      numero_parcelas?: number | null;
      valor_tabela?: number | null;
      desconto_valor?: number | null;
      desconto_concedido_por?: string | null;
      desconto_motivo?: string | null;
    }) => {
      console.log('Criando orçamento:', dados);

      let clienteId = dados.cliente_id;
      if (!clienteId && dados.projeto_id) {
        const { data: projeto } = await supabase
          .from('projetos')
          .select('cliente_id')
          .eq('id', dados.projeto_id)
          .maybeSingle();
        clienteId = projeto?.cliente_id ?? undefined;
      }

      if (!clienteId) {
        throw new Error('cliente_id é obrigatório');
      }

      if (!dados.valor_total || dados.valor_total <= 0) {
        throw new Error('valor_total deve ser maior que zero');
      }

      const descontoValor = Math.max(0, Number(dados.desconto_valor) || 0);
      const valorTabela =
        dados.valor_tabela != null && Number(dados.valor_tabela) > 0
          ? Number(dados.valor_tabela)
          : null;

      const orcamentoData: Record<string, unknown> = {
        cliente_id: clienteId,
        projeto_id: dados.projeto_id || null,
        valor_total: dados.valor_total,
        observacoes: dados.observacoes || null,
        plano_id: dados.plano_id || null,
        quantidade_placas: dados.quantidade_placas || null,
        valor_seguro: dados.valor_seguro || null,
        vigencia_anos: dados.vigencia_anos || null,
        forma_pagamento: dados.forma_pagamento || null,
        parcelas_cartao: dados.parcelas_cartao ?? null,
        numero_parcelas: dados.numero_parcelas ?? dados.parcelas_cartao ?? null,
        valor_tabela: valorTabela,
        desconto_valor: descontoValor > 0 ? descontoValor : null,
        desconto_motivo: dados.desconto_motivo?.trim() || null,
        status: 'pendente',
        ativo: true,
        prazo_aprovacao: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 dias a partir de agora
      };
      // Sempre registrar quem criou o orçamento (criador = qualquer usuário logado)
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) orcamentoData.vendedor_id = user.id;
      if (descontoValor > 0) {
        orcamentoData.desconto_concedido_por = dados.desconto_concedido_por || user?.id || null;
        orcamentoData.desconto_aplicado_em = new Date().toISOString();
      }

      console.log('Dados do orçamento a inserir:', orcamentoData);

      console.log('Tentando inserir orçamento com dados:', orcamentoData);
      
      let orcamentoInserido;
      
      try {
        console.log('🔍 Tentando inserir com dados:', JSON.stringify(orcamentoData, null, 2));
        
        // Primeiro, inserir o orçamento sem select complexo
        const { data, error: insertError } = await supabase
          .from('orcamentos')
          .insert(orcamentoData)
          .select('*')
          .single();

        if (insertError) {
          console.error('❌ Erro ao criar orçamento:', insertError);
          console.error('📋 Detalhes do erro:', {
            code: insertError.code,
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint
          });
          console.error('🔍 Dados que tentou inserir:', orcamentoData);
          throw insertError;
        }

        orcamentoInserido = data;
        console.log('✅ Orçamento inserido com sucesso:', orcamentoInserido);
      } catch (error) {
        console.error('❌ Erro geral na inserção:', error);
        console.error('❌ Stack trace:', (error as Error).stack);
        throw error;
      }

      // Agora buscar o orçamento com dados do cliente
      const { data: orcamentoCompleto, error: selectError } = await supabase
        .from('orcamentos')
        .select(`
          *,
          cliente:clientes(*)
        `)
        .eq('id', orcamentoInserido.id)
        .single();

      if (selectError) {
        console.error('Erro ao buscar orçamento criado:', selectError);
        // Retornar o orçamento inserido mesmo sem cliente
        return orcamentoInserido;
      }

      console.log('Orçamento criado com sucesso:', orcamentoCompleto);
      return orcamentoCompleto;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      // Disparar evento para atualizar estatísticas do dashboard
      window.dispatchEvent(new CustomEvent('orcamento_criado', { detail: data }));
    }
  });
}

export function useAprovarOrcamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orcamentoId: number) => {
      console.log('🚀 Iniciando aprovação do orçamento:', orcamentoId);
      
      try {
        // Buscar dados do orçamento
        console.log('📋 Buscando dados do orçamento...');
        const { data: orcamento, error: orcError } = await supabase
          .from('orcamentos')
          .select(`
            *,
            cliente:clientes(*)
          `)
          .eq('id', orcamentoId)
          .single();

        if (orcError) {
          console.error('❌ Erro ao buscar orçamento:', orcError);
          throw new Error(`Erro ao buscar orçamento: ${orcError.message}`);
        }

        console.log('✅ Orçamento encontrado:', orcamento);

        // Verificar se já existe projeto vinculado a este orçamento
        console.log('🏗️ Verificando projeto existente vinculado ao orçamento...');
        let projeto = null;

        if (orcamento.projeto_id) {
          const { data: projetoExistentePorId } = await supabase
            .from('projetos')
            .select('*')
            .eq('id', orcamento.projeto_id)
            .maybeSingle();
          if (projetoExistentePorId) {
            projeto = projetoExistentePorId;
          }
        }

        if (!projeto) {
          const { data: projetoExistentePorOrcamento } = await supabase
            .from('projetos')
            .select('*')
            .eq('orcamento_id', orcamentoId)
            .maybeSingle();
          if (projetoExistentePorOrcamento) {
            projeto = projetoExistentePorOrcamento;
          }
        }

        const clienteId = orcamento.cliente_id ?? projeto?.cliente_id ?? null;
        if (!clienteId) {
          throw new Error(
            'Não foi possível identificar o cliente deste orçamento. Vincule um cliente ao projeto antes de aprovar.'
          );
        }

        if (!orcamento.cliente_id) {
          const { error: clienteUpdateError } = await supabase
            .from('orcamentos')
            .update({ cliente_id: clienteId })
            .eq('id', orcamentoId);
          if (clienteUpdateError) {
            throw new Error(`Erro ao vincular cliente ao orçamento: ${clienteUpdateError.message}`);
          }
          orcamento.cliente_id = clienteId;
        }

        // Atualizar status do orçamento para aprovado
        console.log('🔄 Atualizando status do orçamento para aprovado...');
        const { error: updateError } = await supabase
          .from('orcamentos')
          .update({
            status: 'aprovado',
            data_aprovacao: new Date().toISOString(),
            aprovado_por: (await supabase.auth.getUser()).data.user?.id || null
          })
          .eq('id', orcamentoId);

        if (updateError) {
          console.error('❌ Erro ao atualizar status do orçamento:', updateError);
          throw new Error(`Erro ao atualizar status: ${updateError.message}`);
        }

        console.log('✅ Status do orçamento atualizado para aprovado');

        if (!projeto) {
          console.log('🏗️ Nenhum projeto existente encontrado. Criando novo projeto automaticamente...');
          const { data: novoProjeto, error: projetoError } = await supabase
            .from('projetos')
            .insert({
              cliente_id: clienteId,
              orcamento_id: orcamentoId,
              status: 'equipamento',
              data_aprovacao: new Date().toISOString(),
              observacoes: 'Projeto criado automaticamente após aprovação do orçamento'
            })
            .select()
            .single();

          if (projetoError) {
            console.error('❌ Erro ao criar projeto:', projetoError);
            throw new Error(`Erro ao criar projeto: ${projetoError.message}`);
          }

          projeto = novoProjeto;
          console.log('✅ Projeto criado automaticamente:', projeto);
        } else {
          console.log('✅ Reutilizando projeto existente:', projeto.id);

          const updates: Record<string, any> = {
            // Sempre aponta para o orçamento aprovado (atualiza valor do seguro na listagem)
            orcamento_id: orcamentoId,
          };
          if (!projeto.data_aprovacao) {
            updates.data_aprovacao = new Date().toISOString();
          }
          if (!projeto.status || projeto.status === 'orcamento') {
            updates.status = 'equipamento';
          }

          await supabase
            .from('projetos')
            .update(updates)
            .eq('id', projeto.id);
          console.log('🔄 Projeto existente atualizado com orçamento aprovado:', orcamentoId);
        }

        // Garantir que orçamento saiba qual projeto está vinculado
        if (!orcamento.projeto_id || orcamento.projeto_id !== projeto.id) {
          await supabase
            .from('orcamentos')
            .update({ projeto_id: projeto.id })
            .eq('id', orcamentoId);
        }

        // Criar pedido automaticamente somente se ainda não existir
        console.log('📋 Verificando pedido existente para o orçamento...');
        const { data: pedidoExistente } = await supabase
          .from('pedidos')
          .select('id, numero_pedido, projeto_id, limpeza_status')
          .eq('orcamento_id', orcamentoId)
          .maybeSingle();

        let pedido = pedidoExistente;

        if (!pedidoExistente) {
          console.log('📋 Nenhum pedido existente encontrado. Criando pedido automaticamente...');
          const dataAtual = new Date();
          const numeroPedido = `PED${dataAtual.getFullYear()}${String(dataAtual.getMonth() + 1).padStart(2, '0')}${String(dataAtual.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
          const dataPedido = dataAtual.toISOString().split('T')[0];
          const isRenovacao = Boolean(orcamento.pedido_origem_id);
          const numeroCiclo = isRenovacao
            ? Number(orcamento.numero_ciclo) || 2
            : 1;

          let limpezaStatus: string | null = null;
          if (orcamento.plano_id) {
            const { data: plano } = await supabase
              .from('planos_seguro')
              .select('id, nome, inclui_limpeza')
              .eq('id', orcamento.plano_id)
              .maybeSingle();
            if (planoIncluiLimpeza(plano)) {
              limpezaStatus = 'pendente';
            }
          }

          let vendedorPedido = orcamento.vendedor_id ?? (await supabase.auth.getUser()).data.user?.id ?? null;
          if (isRenovacao) {
            const { data: origemPed } = await supabase
              .from('pedidos')
              .select('vendedor_id')
              .eq('id', orcamento.pedido_origem_id)
              .maybeSingle();
            if (origemPed?.vendedor_id) vendedorPedido = origemPed.vendedor_id;
          }
          
          const { data: novoPedido, error: pedidoError } = await supabase
            .from('pedidos')
            .insert({
              orcamento_id: orcamentoId,
              projeto_id: projeto.id,
              cliente_id: clienteId,
              valor_total: orcamento.valor_total,
              status: 'aprovado',
              data_pedido: dataPedido,
              prazo_entrega: 30,
              vendedor_id: vendedorPedido,
              numero_pedido: numeroPedido,
              tipo_negocio: isRenovacao ? 'renovacao' : 'primeira_venda',
              pedido_anterior_id: isRenovacao ? orcamento.pedido_origem_id : null,
              numero_ciclo: numeroCiclo,
              situacao_renovacao: 'em_vigencia',
              ...(isRenovacao ? { data_renovacao: dataPedido } : {}),
              ...(limpezaStatus ? { limpeza_status: limpezaStatus } : {}),
            })
            .select()
            .single();

          if (pedidoError) {
            console.error('❌ Erro ao criar pedido:', pedidoError);
            throw new Error(`Erro ao criar pedido: ${pedidoError.message}`);
          }

          pedido = novoPedido;
          console.log('✅ Pedido criado automaticamente:', pedido);
        } else {
          const pedidoUpdates: Record<string, unknown> = {};
          if (!pedidoExistente.projeto_id || pedidoExistente.projeto_id !== projeto.id) {
            pedidoUpdates.projeto_id = projeto.id;
          }
          if (clienteId) {
            pedidoUpdates.cliente_id = clienteId;
          }
          // Backfill limpeza se o plano inclui e o pedido ainda não tem status
          if (!pedidoExistente.limpeza_status && orcamento.plano_id) {
            const { data: plano } = await supabase
              .from('planos_seguro')
              .select('id, nome, inclui_limpeza')
              .eq('id', orcamento.plano_id)
              .maybeSingle();
            if (planoIncluiLimpeza(plano)) {
              pedidoUpdates.limpeza_status = 'pendente';
            }
          }
          if (Object.keys(pedidoUpdates).length > 0) {
            await supabase
              .from('pedidos')
              .update(pedidoUpdates)
              .eq('id', pedidoExistente.id);
          }
          console.log('✅ Pedido existente encontrado:', pedidoExistente.id);
        }

        console.log('✅ Pedido criado automaticamente:', pedido);

        if (orcamento.pedido_origem_id && pedido?.id) {
          const dataRenovacao = new Date().toISOString().split('T')[0];
          await supabase
            .from('pedidos')
            .update({
              situacao_renovacao: 'renovado',
              data_renovacao: dataRenovacao,
            })
            .eq('id', orcamento.pedido_origem_id);

          await supabase
            .from('agenda')
            .update({ status: 'concluido' })
            .eq('tipo', 'renovacao_seguro')
            .eq('status', 'pendente')
            .eq('pedido_id', orcamento.pedido_origem_id);

          const { data: eqsOrc } = await supabase
            .from('orcamento_equipamentos')
            .select('equipamento_id, quantidade, preco_unitario')
            .eq('orcamento_id', orcamentoId);

          if (projeto?.id && eqsOrc && eqsOrc.length > 0) {
            const { data: eqsProj } = await supabase
              .from('projeto_equipamentos')
              .select('equipamento_id')
              .eq('projeto_id', projeto.id);
            const existentes = new Set((eqsProj || []).map((e: { equipamento_id: number }) => e.equipamento_id));
            const extras = eqsOrc.filter((e: { equipamento_id: number }) => !existentes.has(e.equipamento_id));
            if (extras.length > 0) {
              await supabase.from('projeto_equipamentos').insert(
                extras.map((e: { equipamento_id: number; quantidade: number; preco_unitario?: number }) => ({
                  projeto_id: projeto.id,
                  equipamento_id: e.equipamento_id,
                  quantidade: e.quantidade,
                  preco_unitario: e.preco_unitario ?? 0,
                }))
              );
            }
          }
        }

        // Contrato e transação financeira serão criados manualmente através dos botões
        console.log('📋 Contrato e transação financeira devem ser criados manualmente através dos botões na listagem de pedidos');
        
        return { orcamento, projeto, pedido };
      } catch (error) {
        console.error('💥 Erro geral na aprovação:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      console.log('🎉 Aprovação concluída com sucesso!');
      console.log('🔄 Invalidando queries...');
      
      // Invalidar queries necessárias
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-limpeza'] });
      
      // Forçar refetch imediato
      setTimeout(() => {
        console.log('🔄 Forçando refetch das queries...');
        queryClient.refetchQueries({ queryKey: ['orcamentos'] });
        queryClient.refetchQueries({ queryKey: ['projetos'] });
        queryClient.refetchQueries({ queryKey: ['pedidos'] });
      }, 100);
    },
    onError: (error, variables) => {
      console.error('💥 Erro na aprovação do orçamento:', error);
      console.error('ID do orçamento:', variables);
    }
  });
}

/** Salva vistoria (1-4 fotos ou 1 PDF) e define status aguardando_aprovacao_vistoria. Usado pelo vendedor. */
export function useSalvarVistoriaOrcamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      orcamentoId: number;
      arquivos: Array<{ file: File; nome: string }>;
    }) => {
      const { orcamentoId, arquivos } = params;
      if (!arquivos.length || arquivos.length > 4) {
        throw new Error('Anexe de 1 a 4 fotos ou 1 PDF.');
      }
      const isPdf = arquivos[0].file.type === 'application/pdf';
      if (isPdf && arquivos.length > 1) {
        throw new Error('Para PDF, envie apenas 1 arquivo.');
      }
      if (!isPdf && arquivos.some(a => a.file.type !== 'application/pdf' && !a.file.type.startsWith('image/'))) {
        throw new Error('Envie apenas imagens (1 a 4) ou 1 PDF.');
      }

      const basePath = `orcamentos-vistoria/${orcamentoId}`;
      const vistoriaUrls: Array<{ url: string; nome: string }> = [];

      for (const { file, nome } of arquivos) {
        const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const path = `${basePath}/${fileName}`;
        const { publicUrl } = await uploadFileToStorage(path, file, {
          buckets: ["uploads"],
          upsert: true,
        });
        vistoriaUrls.push({ url: publicUrl, nome: file.name });
      }

      const { error: updateError } = await supabase
        .from('orcamentos')
        .update({
          vistoria_urls: vistoriaUrls,
          status: 'aguardando_aprovacao_vistoria',
          motivo_rejeicao_vistoria: null
        })
        .eq('id', orcamentoId);

      if (updateError) throw new Error(updateError.message);
      return { orcamentoId, vistoriaUrls };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
    }
  });
}

/** Rejeita orçamento em análise de vistoria (operador/admin/TI). */
export function useRejeitarOrcamentoVistoria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { orcamentoId: number; motivo?: string }) => {
      const { orcamentoId, motivo } = params;
      const { error } = await supabase
        .from('orcamentos')
        .update({
          status: 'rejeitado',
          motivo_rejeicao_vistoria: motivo || null
        })
        .eq('id', orcamentoId);

      if (error) throw new Error(error.message);
      return orcamentoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
    }
  });
}

export function useAtualizarOrcamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orcamentoData: {
      id: number;
      cliente_id?: number;
      valor_total?: number;
      valor_seguro?: number | null;
      observacoes?: string;
      plano_id?: string | null;
      quantidade_placas?: number;
      vigencia_anos?: number;
      forma_pagamento?: string | null;
      parcelas_cartao?: number;
      numero_parcelas?: number;
      valor_tabela?: number | null;
      desconto_valor?: number | null;
      desconto_concedido_por?: string | null;
      desconto_motivo?: string | null;
      desconto_aplicado_em?: string | null;
    }) => {
      const { id, ...updateData } = orcamentoData;

      if ('cliente_id' in updateData) {
        const cid = updateData.cliente_id;
        if (cid == null || !Number.isFinite(Number(cid)) || Number(cid) <= 0) {
          delete updateData.cliente_id;
        }
      }

      if ('valor_total' in updateData) {
        const v = Number(updateData.valor_total);
        if (!Number.isFinite(v) || v <= 0) delete updateData.valor_total;
      }

      if ('quantidade_placas' in updateData) {
        const q = Number(updateData.quantidade_placas);
        if (!Number.isFinite(q) || q <= 0) delete updateData.quantidade_placas;
      }

      const descontoValor = Number(updateData.desconto_valor);
      if (Number.isFinite(descontoValor) && descontoValor > 0) {
        if (!updateData.desconto_aplicado_em) {
          updateData.desconto_aplicado_em = new Date().toISOString();
        }
        if (!updateData.desconto_concedido_por) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.id) updateData.desconto_concedido_por = user.id;
        }
      }
      
      console.log('Atualizando orçamento com dados:', updateData);
      
      const { data, error } = await supabase
        .from('orcamentos')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar orçamento:', error);
        throw error;
      }

      console.log('Orçamento atualizado com sucesso:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    }
  });
}

export function useExcluirOrcamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orcamentoId: number) => {
      console.log('🗑️ Iniciando exclusão do orçamento:', orcamentoId);
      
      try {
        // Verificar se o orçamento pode ser excluído
        const { data: orcamento, error: selectError } = await supabase
          .from('orcamentos')
          .select('status, ativo, projeto_id')
          .eq('id', orcamentoId)
          .single();

        if (selectError) {
          console.error('❌ Erro ao buscar orçamento:', selectError);
          throw new Error(`Erro ao buscar orçamento: ${selectError.message}`);
        }

        // Verificar se o orçamento já foi aprovado
        if (orcamento.status === 'aprovado') {
          throw new Error('Não é possível excluir um orçamento já aprovado');
        }

        // Verificar se o orçamento já foi inativado
        if (!orcamento.ativo) {
          throw new Error('Este orçamento já foi inativado');
        }

        // Realizar soft delete (inativação)
        console.log('🔄 Inativando orçamento...');
        const { error: updateError } = await supabase
          .from('orcamentos')
          .update({
            ativo: false,
            status: 'excluido',
            motivo_inativacao: 'Excluído pelo usuário',
            data_inativacao: new Date().toISOString()
          })
          .eq('id', orcamentoId);

        if (updateError) {
          console.error('❌ Erro ao inativar orçamento:', updateError);
          throw new Error(`Erro ao inativar orçamento: ${updateError.message}`);
        }

        // Limpa vínculo no projeto (evita valor do seguro antigo na listagem)
        await supabase
          .from('projetos')
          .update({ orcamento_id: null })
          .eq('orcamento_id', orcamentoId);

        const projetoId = orcamento.projeto_id ? Number(orcamento.projeto_id) : null;
        if (projetoId) {
          const { data: aprovadoRestante } = await supabase
            .from('orcamentos')
            .select('id')
            .eq('projeto_id', projetoId)
            .eq('status', 'aprovado')
            .eq('ativo', true)
            .order('data_aprovacao', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (aprovadoRestante?.id) {
            await supabase
              .from('projetos')
              .update({ orcamento_id: aprovadoRestante.id })
              .eq('id', projetoId);
          }
        }

        console.log('✅ Orçamento inativado com sucesso');
        return { success: true, message: 'Orçamento excluído com sucesso' };
      } catch (error) {
        console.error('💥 Erro na exclusão do orçamento:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('🎉 Exclusão concluída com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
    },
    onError: (error) => {
      console.error('💥 Erro na exclusão do orçamento:', error);
    }
  });
}

export function useInativarOrcamentoVencido() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      console.log('⏰ Iniciando inativação automática de orçamentos vencidos...');
      
      try {
        // Chamar a função do banco de dados
        const { data, error } = await supabase.rpc('inativar_orcamentos_vencidos');
        
        if (error) {
          console.error('❌ Erro ao executar função de inativação:', error);
          throw new Error(`Erro ao inativar orçamentos vencidos: ${error.message}`);
        }

        console.log('✅ Inativação automática executada com sucesso');
        return { success: true, message: 'Orçamentos vencidos foram inativados automaticamente' };
      } catch (error) {
        console.error('💥 Erro na inativação automática:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('🔄 Invalidando queries após inativação...');
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
    },
    onError: (error) => {
      console.error('💥 Erro na inativação automática:', error);
    }
  });
}
